/**
 * Global location search (geocoding).
 *
 *   Search UI → locationSearchService → GeocoderAdapter → normalised results
 *
 * The UI never talks to a geocoder directly, so swapping provider is a config
 * change plus one adapter — no component edits.
 *
 * CONFIGURATION (all optional, read from the standard CRA environment):
 *   REACT_APP_GEOCODER_PROVIDER  'nominatim' (default) | 'arcgis'
 *   REACT_APP_GEOCODER_URL       overrides the endpoint for the chosen provider
 *   REACT_APP_GEOCODER_KEY       token, only if the deployment's provider needs one
 *
 * Nothing is hard-coded to a host or a key: the default provider (OpenStreetMap
 * Nominatim) needs no credentials, and any deployment can point
 * REACT_APP_GEOCODER_URL at its own geocoder — including an internal one — so
 * the same build runs in dev, UAT, and production.
 */

/** Provider-independent search result. Optional members are only populated
 *  when the chosen geocoder actually returns them. */
export interface LocationResult {
  id: string;
  /** Short primary line, e.g. "Hyderabad". */
  label: string;
  /** Full contextual line, e.g. "Hyderabad, Telangana, India". */
  displayName: string;
  latitude: number;
  longitude: number;
  /** [west, south, east, north] when the provider supplies an extent. */
  boundingBox?: [number, number, number, number];
  city?: string;
  state?: string;
  country?: string;
  /** Provider hint ("city", "administrative", …) used to pick a zoom level. */
  kind?: string;
}

export interface GeocoderAdapter {
  readonly name: string;
  search(query: string, signal: AbortSignal): Promise<LocationResult[]>;
}

const env = (key: string): string | undefined => {
  const value = process.env[key];
  return value && value.trim() !== '' ? value.trim() : undefined;
};

/* ───────────────────────── Nominatim (OpenStreetMap) ─────────────────────── */

const NOMINATIM_DEFAULT_URL = 'https://nominatim.openstreetmap.org/search';

/**
 * Default provider. Chosen because it needs no API key, so a fresh deployment
 * searches correctly without any secret being committed. Deployments with
 * volume requirements should point REACT_APP_GEOCODER_URL at their own
 * Nominatim instance or switch provider.
 */
const nominatimAdapter: GeocoderAdapter = {
  name: 'nominatim',

  async search(query, signal) {
    const url = new URL(env('REACT_APP_GEOCODER_URL') ?? NOMINATIM_DEFAULT_URL);
    url.searchParams.set('q', query);
    url.searchParams.set('format', 'jsonv2');
    url.searchParams.set('addressdetails', '1');
    url.searchParams.set('limit', '8');

    const response = await fetch(url.toString(), {
      signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Geocoder returned ${response.status}`);

    const data = (await response.json()) as any[];
    if (!Array.isArray(data)) return [];

    return data
      .map((entry): LocationResult | null => {
        const latitude = Number(entry?.lat);
        const longitude = Number(entry?.lon);
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

        const address = entry?.address ?? {};
        const city =
          address.city || address.town || address.village || address.municipality || undefined;

        // Nominatim's boundingbox is [south, north, west, east] as strings.
        const box = Array.isArray(entry?.boundingbox) ? entry.boundingbox.map(Number) : null;
        const boundingBox =
          box && box.length === 4 && box.every(Number.isFinite)
            ? ([box[2], box[0], box[3], box[1]] as [number, number, number, number])
            : undefined;

        const displayName: string = entry?.display_name ?? '';

        return {
          id: String(entry?.place_id ?? `${latitude},${longitude}`),
          label: entry?.name || displayName.split(',')[0] || query,
          displayName: displayName || entry?.name || query,
          latitude,
          longitude,
          boundingBox,
          city,
          state: address.state || undefined,
          country: address.country || undefined,
          kind: entry?.addresstype || entry?.type || undefined,
        };
      })
      .filter((result): result is LocationResult => result !== null);
  },
};

/* ──────────────────────────── ArcGIS World Geocoder ──────────────────────── */

const ARCGIS_DEFAULT_URL =
  'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates';

/**
 * For deployments already standardised on Esri. Uses the same token
 * configuration as any other Esri service; without a token the public service
 * still answers for basic candidates.
 */
const arcgisAdapter: GeocoderAdapter = {
  name: 'arcgis',

  async search(query, signal) {
    const url = new URL(env('REACT_APP_GEOCODER_URL') ?? ARCGIS_DEFAULT_URL);
    url.searchParams.set('singleLine', query);
    url.searchParams.set('outFields', 'City,Region,Country,Addr_type');
    url.searchParams.set('maxLocations', '8');
    url.searchParams.set('f', 'json');
    const token = env('REACT_APP_GEOCODER_KEY');
    if (token) url.searchParams.set('token', token);

    const response = await fetch(url.toString(), { signal });
    if (!response.ok) throw new Error(`Geocoder returned ${response.status}`);

    const data = await response.json();
    // Esri reports failures as HTTP 200 with an `error` object.
    if (data?.error) throw new Error(data.error.message || 'Geocoder returned an error');

    return (data?.candidates ?? [])
      .map((candidate: any): LocationResult | null => {
        const latitude = Number(candidate?.location?.y);
        const longitude = Number(candidate?.location?.x);
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

        const extent = candidate?.extent;
        const boundingBox =
          extent &&
          [extent.xmin, extent.ymin, extent.xmax, extent.ymax].every((value: any) =>
            Number.isFinite(value),
          )
            ? ([extent.xmin, extent.ymin, extent.xmax, extent.ymax] as [
                number,
                number,
                number,
                number,
              ])
            : undefined;

        const attributes = candidate?.attributes ?? {};
        const address: string = candidate?.address ?? '';

        return {
          id: `${longitude},${latitude}`,
          label: address.split(',')[0] || address,
          displayName: address,
          latitude,
          longitude,
          boundingBox,
          city: attributes.City || undefined,
          state: attributes.Region || undefined,
          country: attributes.Country || undefined,
          kind: attributes.Addr_type || undefined,
        };
      })
      .filter((result: LocationResult | null): result is LocationResult => result !== null);
  },
};

const ADAPTERS: Record<string, GeocoderAdapter> = {
  nominatim: nominatimAdapter,
  arcgis: arcgisAdapter,
};

export function getGeocoder(): GeocoderAdapter {
  const configured = (env('REACT_APP_GEOCODER_PROVIDER') ?? 'nominatim').toLowerCase();
  const adapter = ADAPTERS[configured];
  if (!adapter) {
    console.warn(
      `[search] Unknown REACT_APP_GEOCODER_PROVIDER "${configured}"; falling back to nominatim.`,
    );
    return nominatimAdapter;
  }
  return adapter;
}

export const locationSearchService = {
  /**
   * Runs a location search.
   * `signal` lets the caller abort superseded requests, which is what stops an
   * older, slower response from overwriting newer suggestions.
   */
  async search(query: string, signal: AbortSignal): Promise<LocationResult[]> {
    const trimmed = query.trim();
    if (trimmed.length < 2) return [];
    return getGeocoder().search(trimmed, signal);
  },
};

/**
 * Picks a map view for a result.
 *
 * An extent is preferred — a country and a street should not land on the same
 * zoom. Falls back to a scale chosen from the result kind when the provider
 * gives coordinates only.
 */
export function getResultView(result: LocationResult): {
  center: [number, number];
  zoom: number;
} {
  const center: [number, number] = [result.latitude, result.longitude];

  if (result.boundingBox) {
    const [west, south, east, north] = result.boundingBox;
    const span = Math.max(Math.abs(east - west), Math.abs(north - south), 0.0005);
    return { center, zoom: Math.max(3, Math.min(17, Math.floor(Math.log2(360 / span) + 1))) };
  }

  const kind = (result.kind ?? '').toLowerCase();
  if (/country/.test(kind)) return { center, zoom: 5 };
  if (/state|region|province/.test(kind)) return { center, zoom: 7 };
  if (/city|town|municipality/.test(kind)) return { center, zoom: 11 };
  if (/village|suburb|neighbourhood/.test(kind)) return { center, zoom: 13 };
  if (/street|road|address|poi|building/.test(kind)) return { center, zoom: 16 };
  return { center, zoom: 12 };
}
